/**
 * Background Loading Service
 * 
 * Coordinates background loading of non-critical data after the main
 * application has loaded. This prevents API rate limits by ensuring
 * data loads sequentially rather than in parallel.
 */

interface BackgroundLoadingConfig {
  onStageStart?: (stageId: string) => void;
  onStageComplete?: (stageId: string) => void;
  onError?: (stageId: string, error: Error) => void;
}

interface QueuedStage {
  stageId: string;
  priority: number;
  loader: () => Promise<void>;
}

interface PeriodicTask {
  taskId: string;
  intervalMs: number;
  priority: number;
  loader: () => Promise<void>;
  lastRun?: number;
  timerId?: NodeJS.Timeout;
  isRunning?: boolean;
}

class BackgroundLoadingService {
  private queue: QueuedStage[] = [];
  private isProcessing = false;
  private config: BackgroundLoadingConfig = {};
  private abortController?: AbortController;
  private periodicTasks = new Map<string, PeriodicTask>();
  private isActive = true; // Track if user is active (not idle)

  /**
   * Initialize the service with callbacks
   */
  initialize(config: BackgroundLoadingConfig) {
    this.config = config;
    
    // Set up user activity tracking
    this.setupActivityTracking();
  }

  /**
   * Set up user activity tracking to pause periodic tasks when idle
   */
  private setupActivityTracking() {
    let idleTimer: NodeJS.Timeout;
    const IDLE_TIMEOUT = 5 * 60 * 1000; // 5 minutes

    const resetIdleTimer = () => {
      if (!this.isActive) {
        // console.log('👤 User activity detected - resuming periodic tasks');
        this.isActive = true;
        this.resumePeriodicTasks();
      }
      
      clearTimeout(idleTimer);
      idleTimer = setTimeout(() => {
        // console.log('💤 User idle - pausing periodic tasks');
        this.isActive = false;
        this.pausePeriodicTasks();
      }, IDLE_TIMEOUT);
    };

    // Track user activity
    ['mousedown', 'keydown', 'scroll', 'touchstart'].forEach(event => {
      document.addEventListener(event, resetIdleTimer, { passive: true });
    });

    // Start the idle timer
    resetIdleTimer();
  }

  /**
   * Add a stage to the background loading queue
   */
  addToQueue(stageId: string, priority: number, loader: () => Promise<void>) {
    // Check if already in queue
    if (this.queue.some(item => item.stageId === stageId)) {
      // console.log(`Stage ${stageId} already in background queue`);
      return;
    }

    // console.log(`Adding ${stageId} to background loading queue with priority ${priority}`);
    
    this.queue.push({ stageId, priority, loader });
    
    // Sort by priority (higher priority first)
    this.queue.sort((a, b) => b.priority - a.priority);
    
    // Start processing if not already running
    if (!this.isProcessing) {
      this.processQueue();
    }
  }

  /**
   * Remove a stage from the queue (if user navigates to that page)
   */
  removeFromQueue(stageId: string) {
    this.queue = this.queue.filter(item => item.stageId !== stageId);
    console.log(`Removed ${stageId} from background queue`);
  }

  /**
   * Process the background loading queue
   */
  private async processQueue() {
    if (this.isProcessing || this.queue.length === 0) {
      return;
    }

    this.isProcessing = true;
    this.abortController = new AbortController();

    // Verbose debug log - commented for production
    // console.log(`Starting background loading queue processing...`);

    while (this.queue.length > 0 && !this.abortController.signal.aborted) {
      const item = this.queue.shift();
      if (!item) continue;

      try {
        // Background loading ${item.stageId}...
        this.config.onStageStart?.(item.stageId);
        
        // Add a small delay before starting to ensure critical path has completed
        await new Promise(resolve => setTimeout(resolve, 200));
        
        if (this.abortController.signal.aborted) {
          break;
        }

        await item.loader();

        // Verbose debug log - commented for production
        // console.log(`Background loading completed for ${item.stageId}`);
        this.config.onStageComplete?.(item.stageId);
        
        // Delay between stages to prevent API overload
        if (this.queue.length > 0) {
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      } catch (error) {
        console.error(`❌ Background loading failed for ${item.stageId}:`, error);
        this.config.onError?.(item.stageId, error as Error);
      }
    }

    this.isProcessing = false;
    // Verbose debug log - commented for production
    // console.log(`Background loading queue processing completed`);
  }

  /**
   * Clear the queue and stop processing
   */
  clear() {
    this.queue = [];
    this.abortController?.abort();
    this.isProcessing = false;
    console.log(`Background loading queue cleared`);
  }

  /**
   * Get current queue status
   */
  getQueueStatus() {
    return {
      isProcessing: this.isProcessing,
      queueLength: this.queue.length,
      queue: this.queue.map(item => ({ stageId: item.stageId, priority: item.priority }))
    };
  }

  /**
   * Add a periodic task that runs at specified intervals
   * @param taskId Unique identifier for the task
   * @param intervalMs Interval in milliseconds (minimum 60000ms / 1 minute)
   * @param priority Priority for queue processing when task runs
   * @param loader Function to execute periodically
   */
  addPeriodicTask(taskId: string, intervalMs: number, priority: number, loader: () => Promise<void>) {
    // Enforce minimum interval to prevent excessive API calls
    const safeInterval = Math.max(intervalMs, 60000); // Minimum 1 minute
    
    if (this.periodicTasks.has(taskId)) {
      // console.log(`Updating existing periodic task: ${taskId}`);
      this.removePeriodicTask(taskId);
    }

    // Adding periodic task: ${taskId} (interval: ${safeInterval}ms, priority: ${priority})
    
    const task: PeriodicTask = {
      taskId,
      intervalMs: safeInterval,
      priority,
      loader,
      lastRun: 0,
      isRunning: false
    };

    // Set up the periodic execution
    const executeTask = async () => {
      // Skip if user is idle or task is already running
      if (!this.isActive || task.isRunning) {
        return;
      }

      // Check if enough time has passed since last run
      const now = Date.now();
      if (task.lastRun && (now - task.lastRun) < safeInterval) {
        return;
      }

      task.isRunning = true;
      // Executing periodic task: ${taskId}
      
      // Add to queue for execution
      this.addToQueue(`${taskId}-periodic`, priority, async () => {
        try {
          await loader();
          task.lastRun = Date.now();
          // Periodic task completed: ${taskId}
        } catch (error) {
          console.error(`❌ Periodic task failed: ${taskId}`, error);
        } finally {
          task.isRunning = false;
        }
      });
    };

    // Set up interval without immediate execution
    // The first execution will happen after the specified interval
    task.timerId = setInterval(executeTask, safeInterval);
    
    this.periodicTasks.set(taskId, task);
  }

  /**
   * Remove a periodic task
   */
  removePeriodicTask(taskId: string) {
    const task = this.periodicTasks.get(taskId);
    if (task) {
      if (task.timerId) {
        clearInterval(task.timerId);
      }
      this.periodicTasks.delete(taskId);
      console.log(`Removed periodic task: ${taskId}`);
    }
  }

  /**
   * Pause all periodic tasks (when user is idle)
   */
  private pausePeriodicTasks() {
    this.periodicTasks.forEach(task => {
      if (task.timerId) {
        clearInterval(task.timerId);
        task.timerId = undefined;
      }
    });
  }

  /**
   * Resume all periodic tasks (when user is active)
   */
  private resumePeriodicTasks() {
    this.periodicTasks.forEach((task, taskId) => {
      if (!task.timerId) {
        // Recreate the interval
        const executeTask = async () => {
          if (!this.isActive || task.isRunning) {
            return;
          }

          const now = Date.now();
          if (task.lastRun && (now - task.lastRun) < task.intervalMs) {
            return;
          }

          task.isRunning = true;
          // Executing periodic task: ${taskId}
          
          this.addToQueue(`${taskId}-periodic`, task.priority, async () => {
            try {
              await task.loader();
              task.lastRun = Date.now();
              // Periodic task completed: ${taskId}
            } catch (error) {
              console.error(`❌ Periodic task failed: ${taskId}`, error);
            } finally {
              task.isRunning = false;
            }
          });
        };

        task.timerId = setInterval(executeTask, task.intervalMs);
      }
    });
  }

  /**
   * Clear all periodic tasks
   */
  clearPeriodicTasks() {
    this.periodicTasks.forEach((task, taskId) => {
      this.removePeriodicTask(taskId);
    });
  }
}

// Export singleton instance
export const backgroundLoadingService = new BackgroundLoadingService();

export default backgroundLoadingService;